import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { money, perWatt, fmtDate } from "@/lib/format";
import { LineChart, Line, YAxis } from "recharts";

function DeltaChip({ pct, testid }) {
  if (pct === null || pct === undefined) return null;
  const up = pct > 0;
  const tone = pct === 0 ? "text-slate-500 bg-slate-100" : up ? "text-rose-700 bg-rose-50" : "text-emerald-700 bg-emerald-50";
  return (
    <span className={`text-[10px] font-semibold tabular-nums rounded px-1.5 py-0.5 ${tone}`} data-testid={testid}>
      {up ? "+" : ""}{pct}%
    </span>
  );
}

// Price trajectory for a bid: every price sent to the client after each revision, plus the working number.
export function PriceTrajectoryCard({ bidId, refreshKey }) {
  const [pts, setPts] = useState(null);
  const [outcome, setOutcome] = useState(null);
  useEffect(() => {
    api.get(`/bids/${bidId}/price-history`).then((r) => { setPts(r.data?.points || []); setOutcome(r.data?.outcome || null); }).catch(() => setPts([]));
  }, [bidId, refreshKey]);
  if (pts === null) return null;
  const chart = pts.filter((p) => p.total != null).map((p, i) => ({ i, v: Number(p.total) }));
  const first = chart[0]?.v;
  const last = chart[chart.length - 1]?.v;
  const overall = chart.length > 1 && first ? Math.round((1000 * (last - first)) / first) / 10 : null;
  return (
    <Card className="p-5" data-testid="price-trajectory-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Price history</h2>
        {overall !== null && <span className="flex items-center gap-1 text-[11px] text-slate-400">overall <DeltaChip pct={overall} testid="price-trajectory-overall" /></span>}
      </div>
      <p className="text-xs text-slate-400 mb-3">Each price sent to the client, oldest first.</p>
      {pts.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="price-trajectory-empty">No price yet — each revision you send lands here.</div>
      ) : (
        <>
          {chart.length > 1 && (
            <div className="mb-3 -mx-1" data-testid="price-trajectory-chart">
              <LineChart width={260} height={48} data={chart} margin={{ top: 6, bottom: 6, left: 4, right: 4 }}>
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Line type="monotone" dataKey="v" stroke="#003E87" strokeWidth={2} dot={{ r: 2.5, fill: "#003E87" }} isAnimationActive={false} />
              </LineChart>
            </div>
          )}
          <div className="space-y-1.5">
            {pts.map((p, i) => (
              <div key={i} className={`flex items-center justify-between gap-3 rounded px-2.5 py-1.5 ${p.current ? "bg-[#003E87]/5 border border-[#003E87]/15" : "border border-slate-100"}`} data-testid={`price-point-${i}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{p.label}</div>
                  <div className="text-[10px] text-slate-400 truncate">{p.at ? fmtDate(p.at) : "—"}{p.by ? ` · ${p.by}` : ""}{p.cost_per_watt ? ` · ${perWatt(p.cost_per_watt)}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-mono font-semibold tabular-nums ${p.current ? "text-[#003E87]" : "text-slate-800"}`}>{money(p.total)}</div>
                  <DeltaChip pct={p.delta_pct} testid={`price-point-delta-${i}`} />
                </div>
              </div>
            ))}
          </div>
          {outcome?.outcome === "lost" && (outcome.winner || outcome.winner_price != null) && (
            <div className="mt-3 rounded border border-rose-100 bg-rose-50/60 px-2.5 py-2 text-xs" data-testid="price-trajectory-winner">
              <div className="flex items-center justify-between gap-2">
                <span className="text-rose-800 font-medium truncate">Won by {outcome.winner || "another bidder"}</span>
                {outcome.winner_price != null && <span className="font-mono font-semibold text-rose-900">{money(outcome.winner_price)}</span>}
              </div>
              {outcome.gap_to_winner != null && (
                <div className="text-[10px] text-rose-700 mt-0.5" data-testid="price-trajectory-gap">
                  Our last price was {money(Math.abs(outcome.gap_to_winner))} {outcome.gap_to_winner >= 0 ? "higher" : "lower"}{outcome.gap_to_winner_pct != null ? ` (${outcome.gap_to_winner_pct > 0 ? "+" : ""}${outcome.gap_to_winner_pct}%)` : ""}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
